import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { MSelectCard } from "@/components/ui/atoms/MSelectCard";
import styles from "../reminder.module.css";
import MTopBar from "@/components/ui/atoms/MTopBar";
import MHeader from "@/components/ui/organisms/MHeader";
import headerStyles from "@/components/ui/organisms/MHeader.module.css";
import { getMemberInfo } from "@/hooks/useMembers";
import { MButton } from "@/components/ui/atoms/MButtons";
import MFooterNavigation from "@/components/ui/atoms/MFooterNavigation";
import { useReminders } from "@/hooks/useReminders";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import AutoCompleteInput from "@/components/ui/organisms/AutoCompleteInput";
import { typeAheadService } from "@/services/type-ahead.service";
import {
  CreateReminder,
  ReminderFrequency,
  Reminders,
  ReminderSubType,
  ReminderType,
  UpdateReminder,
} from "@/types/reminders";
import {
  getTimezone,
  initDate,
  inputFieldTime,
  updateTime,
} from "@/utils/date-utils";
import {
  getFrequencyText,
  getPossessiveName,
  paintDropDownFrequencySelectField,
  paintEventDateField,
  paintEventForField as paintEventForField,
  paintEventTimeField,
  paintFrequencyDaysField,
  paintFrequencySpecificDaysField,
  ReminderInputStage,
} from "../remindersHelper";
import ReminderSummaryScreen from "../ReminderSummaryScreen";
import {
  IconDoctor,
  IconLabTest,
  IconTherapySession,
  IconHealthCheckup,
} from "@/components/icons";
import { goBackOneLevel } from "@/utils/nav-utils";
import { DEFAULT_TIME_12HR } from "@/utils/constants";
import { format, isAfter, isBefore, isPast } from "date-fns";
import { getErrorMessage } from "@/utils/error";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
  getReminderAppointmentScreenName,
} from "@/constants/analytics.constants";
import { useKeyboardScrollToTop } from "@/hooks/useKeyboardScrollToTop";
import { useFixMobileKeyboard } from "@/hooks/useFixMobileKeyboard";

// Every subtype is now just two stages: a single details screen (name/
// frequency/date/time as applicable) then the final summary screen — the
// reminder date/time (previously their own REMINDER_DETAILS screen) are
// now collected on the summary screen instead, via ReminderSummaryScreen
// (Figma 5257-61120 / 5257-61561 / 5257-61732 collapse what used to be
// multiple screens into one "details" screen per type).
const CategoryFlow: Record<ReminderSubType, ReminderInputStage[]> = {
  DOCTOR_VISIT: [ReminderInputStage.START, ReminderInputStage.ADD],
  LAB_TEST: [ReminderInputStage.START, ReminderInputStage.ADD],
  THERAPY: [ReminderInputStage.START, ReminderInputStage.ADD],
  HEALTH_CHECKUP: [ReminderInputStage.START, ReminderInputStage.ADD],
};

/**
 * Appointment Reminder Creation Page
 * Allows users to select an appointment type to create a reminder for
 */
const AppointmentReminder: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const reminder: Reminders = location.state?.reminder;
  const isEditing = !!reminder;
  const [appointmentType, setAppointmentType] =
    useState<ReminderSubType | null>(reminder?.subType ?? null);
  const [flowIndex, setFlowIndex] = useState<number>(
    isEditing && appointmentType ? CategoryFlow[appointmentType].length - 1 : 0
  );

  // Use keyboard scroll-to-top hook
  // useKeyboardScrollToTop([appointmentType, flowIndex]);

  // Fix mobile keyboard issues
  useFixMobileKeyboard();
  const [eventFor, setEventFor] = useState<string | null>(
    reminder?.reminderFor ?? null
  );
  const [eventDate, setEventDate] = useState<Date | null>(
    initDate(reminder?.eventDate, reminder?.timezone)
  );
  const [eventTime, setEventTime] = useState<string | null>(
    reminder?.eventTime ?? null
  );
  const [reminderDate, setReminderDate] = useState<Date | null>(
    initDate(reminder?.startDate, reminder?.timezone)
  );
  const [reminderTime, setReminderTime] = useState<string | null>(
    inputFieldTime(reminder?.startDate, undefined, false, reminder?.timezone)
  );
  const [reminderEndDate, setReminderEndDate] = useState<Date | null>(
    initDate(reminder?.endDate, reminder?.timezone)
  );
  const [notes, setNotes] = useState<string | null>(reminder?.notes ?? null);
  const [isCritical, setIsCritical] = useState<boolean>(
    reminder ? reminder.tier === "CRITICAL" : false
  );
  const [selectedDays, setSelectedDays] = useState<string[]>(
    reminder?.selectedDays?.map((day) => day.toString()) ?? []
  );
  const [showSetDays, setShowSetDays] = useState<boolean>(false);
  const [frequencyValue, setFrequencyValue] = useState<number>(
    reminder?.frequencyValue ? reminder.frequencyValue : 1
  );
  const { addReminder, updateReminder, totalCount, fetchReminders } =
    useReminders(memberInfo.id);
  const [options, setOptions] = useState<{ value: string }[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [frequency, setFrequency] = useState<ReminderFrequency | null>(
    reminder?.frequency ?? null
  );
  const [reminderTimeError, setReminderTimeError] = useState<string | null>(
    null
  );

  // Fetch reminder count on mount
  useEffect(() => {
    fetchReminders();
  }, []);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_APPOINTMENT,
    });
  }, []);

  useEffect(() => {
    if (appointmentType && appointmentType == ReminderSubType.HEALTH_CHECKUP) {
      setEventFor(t("reminders.HEALTH_CHECKUP.eventFor"));
    }
  }, [appointmentType]);

  const getCategoryKey = (): string => {
    switch (appointmentType) {
      case ReminderSubType.LAB_TEST:
        return "lab_tests_" + memberInfo.derivedMemberType.toLowerCase();
      case ReminderSubType.THERAPY:
        return "therapy_" + memberInfo.derivedMemberType.toLowerCase();
      default:
        return "";
    }
  };

  const getIcon = (type: ReminderSubType, color?: string): React.ReactNode => {
    switch (type) {
      case ReminderSubType.DOCTOR_VISIT:
        return <IconDoctor size={24} color={color} />;
      case ReminderSubType.THERAPY:
        return <IconTherapySession size={24} color={color} />;
      case ReminderSubType.HEALTH_CHECKUP:
        return <IconHealthCheckup size={24} color={color} />;
      case ReminderSubType.LAB_TEST:
        return <IconLabTest size={24} color={color} />;
      default:
        return "";
    }
  };

  // Gradient box behind each subtype's icon on the type-picker cards and the
  // final-stage recap card (Figma 5257-61382 / 5257-61512).
  const getTypeIconGradientClass = (type: ReminderSubType): string => {
    switch (type) {
      case ReminderSubType.DOCTOR_VISIT:
        return styles.typeIconGradientDoctor;
      case ReminderSubType.HEALTH_CHECKUP:
        return styles.typeIconGradientHealthCheckup;
      case ReminderSubType.LAB_TEST:
        return styles.typeIconGradientLabTest;
      case ReminderSubType.THERAPY:
        return styles.typeIconGradientTherapy;
      default:
        return "";
    }
  };

  const paintTypeIcon = (type: ReminderSubType): React.ReactNode => (
    <div className={`${styles.typeIconBox} ${getTypeIconGradientClass(type)}`}>
      {getIcon(type, "var(--brand-colours-indigo-indigo-500, #8764BB)")}
    </div>
  );

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setFetched(false);
  };

  const fetchOptions = async () => {
    if (fetched) {
      return;
    }
    try {
      setLoading(true);
      const res = await typeAheadService.searchInCategory(getCategoryKey(), {
        search: query,
      });
      // Convert string array to object array
      setOptions(res.map((item) => ({ value: item })));
      setFetched(true);
    } catch (error) {
      showToast({
        message: "Error fetching vaccination names",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAppointmentSelect = (value: ReminderSubType) => {
    if (!value) {
      return;
    }
    resetAllValues();
    setAppointmentType(value);

    // Track appointment type selection
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: getReminderAppointmentScreenName(value, "select"),
    });

    setFlowIndex(0);
    nextStage();
  };

  const resetAllValues = () => {
    setEventFor(null);
    setEventDate(null);
    setEventTime(null);
    setReminderDate(null);
    setReminderTime(null);
    setReminderEndDate(null);
    setNotes(null);
    setSelectedDays([]);
    setShowSetDays(false);
    setFrequencyValue(1);
    setFrequency(null);
    setFetched(false);
    setQuery("");
  };

  const handleBack = () => {
    if (!appointmentType || isEditing) {
      navigate(goBackOneLevel(location.pathname));
      return;
    }
    const nextStageVal = flowIndex - 1;
    if (nextStageVal < 0) {
      setAppointmentType(null);
    } else {
      setFlowIndex(nextStageVal);
    }
  };

  const validateCurrentStage = (): boolean => {
    switch (CategoryFlow[appointmentType][flowIndex]) {
      case ReminderInputStage.START:
        return !isStartInvalid();
      default:
        return false;
    }
  };

  // Show toast message for error notification
  const showErrorToast = (_: string) => {
    showToast({
      message: t("errors.toast"),
      variant: "error",
    });
  };

  const nextStage = () => {
    if (!appointmentType) {
      return;
    }
    const nextStageVal = flowIndex + 1;
    if (nextStageVal > CategoryFlow[appointmentType].length - 1) {
      showErrorToast(t("errors.maxStageReached"));
    } else {
      if (validateCurrentStage()) {
        setFlowIndex(nextStageVal);

        // Track summary stage
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: getReminderAppointmentScreenName(
            appointmentType,
            "summary"
          ),
        });

        // Auto-scroll to top when moving to next stage
        setTimeout(() => {
          document
            .querySelector("main")
            ?.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      }
    }
  };

  const paintTypeAheadInput = (
    disabled: boolean = false,
    titleKey?: string
  ) => {
    return (
      <div className="w-full">
        <AutoCompleteInput
          label={t(
            `reminders.${appointmentType}.typeAhead${titleKey ? titleKey : ""}`
          )}
          data={options}
          getOptionLabel={(item) => item.value}
          onSelect={(item) => {
            // Handle both string (from allowCustomInput) and object cases
            if (typeof item === "string") {
              setEventFor(item);
            } else {
              setEventFor(item.value);
            }
          }}
          value={eventFor}
          onChange={(value) => handleQueryChange(value)}
          onSearch={fetchOptions}
          loading={loading}
          placeholder={t(`reminders.${appointmentType}.typeAheadPlaceholder`)}
          allowCustomInput={true}
          minLength={1}
          showAllOnFocus={true}
          disabled={disabled}
        />
      </div>
    );
  };

  // pass a function to check for disable state
  const paintNextButton = (disableCheck: () => boolean) => {
    return (
      <MButton
        variant="filled"
        size="lg"
        onClick={() => nextStage()}
        className={styles.nextButton}
        disabled={disableCheck()}
        text={t("common.next")}
      />
    );
  };

  const eventDateField = (
    labelKey: string = "appointment",
    isSummaryDisplay: boolean = false
  ) => {
    return paintEventDateField(
      eventDate,
      setEventDate,
      t,
      labelKey,
      isSummaryDisplay
    );
  };

  const eventTimeField = (
    labelKey: string = "appointment",
    isSummaryDisplay: boolean = false
  ) => {
    return paintEventTimeField(
      eventDate,
      eventTime,
      setEventTime,
      t,
      labelKey,
      isSummaryDisplay
    );
  };

  const eventForField = () => {
    return paintEventForField(eventFor, setEventFor, t, appointmentType);
  };

  const paintHeader = (
    title: string,
    description?: string,
    addSpacer: boolean = true
  ) => {
    return (
      <>
        <MHeader
          module="reminders"
          memberInfo={memberInfo}
          title={title}
          description={description ? description : undefined}
          useParentGradient={false}
          className={
            isFinalStage
              ? headerStyles.stickyBelowTopBarWithGradient
              : !appointmentType
              ? headerStyles.stickyBelowTopBarWithFooter
              : headerStyles.stickyBelowTopBar
          }
        />
        {addSpacer && <MSpacer />}
      </>
    );
  };

  const handleSetDaysSelect = (options: string[]) => {
    setSelectedDays(options);
    setShowSetDays(false);
  };

  const frequencySpecificDaysField = () => {
    return paintFrequencySpecificDaysField(
      selectedDays,
      setShowSetDays,
      showSetDays,
      handleSetDaysSelect,
      t
    );
  };

  const frequencyDaysField = () => {
    return paintFrequencyDaysField(
      frequencyValue,
      setFrequencyValue,
      t,
      "appointment"
    );
  };

  const [showFrequencyDrawer, setShowFrequencyDrawer] = useState(false);

  // "How often" — a dropdown drawer (Figma 5257-61749), not the card-based
  // picker used elsewhere, and answered inline on the same details screen
  // rather than a separate stage.
  const frequencyDropdownField = () => {
    return paintDropDownFrequencySelectField(
      frequency,
      setFrequency,
      showFrequencyDrawer,
      setShowFrequencyDrawer,
      t,
      "THERAPY",
      undefined,
      t("reminders.THERAPY.frequencyLabel")
    );
  };

  // Recap card label on the final summary screen — "Dr. {name}" for doctor
  // visits, otherwise the entered name/test/therapy as-is.
  const getRecapLabel = (): string => {
    if (appointmentType === ReminderSubType.DOCTOR_VISIT) {
      return `Dr. ${eventFor}`;
    }
    return eventFor || "";
  };

  // Recap card secondary line — therapy (recurring) shows its frequency
  // like vitals/medication do; the one-off types show a plain
  // "Appointment on {date}" caption instead (Figma 5257-61512).
  const getRecapSecondaryText = (): string => {
    if (appointmentType === ReminderSubType.THERAPY) {
      return getFrequencyText(frequency, selectedDays, frequencyValue, t);
    }
    return eventDate
      ? t("reminders.appointment.appointmentOnLabel", {
          date: format(eventDate, "dd-MMMM yyyy"),
        })
      : "";
  };

  // Every subtype's details + summary screens now share the same
  // personalized header copy (Figma: "{Member}'s {type} reminder" +
  // "Fill in the details below..." / "Add the timing details below...").
  const getScreenTitle = (): string => {
    if (!appointmentType) return "";
    const memberName = getPossessiveName(memberInfo, t);
    return `${memberName} ${t(
      `reminders.${appointmentType}.detailsTitleSuffix`
    )}`;
  };

  const paintStartInput = () => {
    switch (appointmentType) {
      case ReminderSubType.DOCTOR_VISIT:
        // Figma 5257-61366: name, date, time only — notes and reminder
        // date/time moved to the final/summary stage instead of being
        // asked twice or on their own separate screen.
        return (
          <>
            {eventForField()}
            {eventDateField()}
            {eventTimeField()}
          </>
        );
      case ReminderSubType.HEALTH_CHECKUP:
        // Figma 5257-61120: date + time only.
        return (
          <>
            {eventDateField()}
            {eventTimeField()}
          </>
        );
      case ReminderSubType.THERAPY:
        // Figma 5257-61732: name, how-often, date, time — all on one
        // screen, collapsing what used to be four separate stages.
        return (
          <>
            {paintTypeAheadInput()}
            {frequencyDropdownField()}
            {frequency === ReminderFrequency.SPECIFIC_DAYS &&
              frequencySpecificDaysField()}
            {frequency === ReminderFrequency.DAY_FREQUENCY &&
              frequencyDaysField()}
            {eventDateField()}
            {eventTimeField()}
          </>
        );
      case ReminderSubType.LAB_TEST:
        // Figma 5257-61561: test name, date, time.
        return (
          <>
            {paintTypeAheadInput()}
            {eventDateField()}
            {eventTimeField()}
          </>
        );
    }
  };

  const isStartInvalid = (): boolean => {
    if (eventFor === null || eventDate === null) return true;
    if (eventTime && isPast(updateTime(eventDate, eventTime))) return true;
    if (appointmentType === ReminderSubType.THERAPY) {
      if (frequency === null) return true;
      if (
        frequency === ReminderFrequency.SPECIFIC_DAYS &&
        selectedDays.length === 0
      )
        return true;
      if (
        frequency === ReminderFrequency.DAY_FREQUENCY &&
        (frequencyValue === 0 || frequencyValue > 60)
      )
        return true;
    }
    return false;
  };

  const isReminderDetailsInvalid = (): boolean => {
    if (reminderDate === null) {
      return true;
    }

    // Check if end date is before start date (skipped for ONLY_ONCE — single event has no end date)
    if (
      [ReminderSubType.THERAPY].includes(appointmentType) &&
      frequency !== ReminderFrequency.ONLY_ONCE &&
      reminderEndDate &&
      reminderDate
    ) {
      if (isBefore(reminderEndDate, reminderDate)) {
        return true;
      }
    }

    if (
      reminderTime === null ||
      (reminderTime && isPast(updateTime(reminderDate, reminderTime)))
    ) {
      return true;
    }

    if (eventTime && eventDate && reminderDate) {
      const eventDate1 = updateTime(eventDate, eventTime);
      const reminderDate1 = updateTime(reminderDate, reminderTime);
      const isInvalid = isAfter(reminderDate1, eventDate1);
      // Only set the error if it's different from current error state
      // to avoid infinite re-renders
      const errorMessage = t("reminders.errors.reminderAfterEventDate");
      if (isInvalid && reminderTimeError !== errorMessage) {
        setReminderTimeError(errorMessage);
      } else if (!isInvalid && reminderTimeError !== null) {
        // Clear the error when validation passes
        setReminderTimeError(null);
      }
      return isInvalid;
    }
    return false;
  };

  const isAddInvalid = (): boolean => {
    return isStartInvalid() || isReminderDetailsInvalid();
  };

  const handleCreateUpdateReminder = async () => {
    const startDate = updateTime(
      reminderDate,
      reminderTime || DEFAULT_TIME_12HR
    );
    const eventDateTime = updateTime(eventDate, eventTime);
    let endDate = startDate;
    switch (appointmentType) {
      case ReminderSubType.THERAPY:
        // ONLY_ONCE has no recurrence: collapse end date onto start date so the
        // backend's window logic treats it as a single-day reminder.
        if (frequency === ReminderFrequency.ONLY_ONCE) {
          endDate = startDate;
        } else {
          endDate = reminderEndDate
            ? updateTime(reminderEndDate, reminderTime || DEFAULT_TIME_12HR)
            : null;
        }
        break;
      default:
        break;
    }
    const selectedDaysValue: number[] =
      appointmentType === ReminderSubType.THERAPY
        ? selectedDays.map((day) => Number(day))
        : [];

    // Get current browser timezone
    const timezone = getTimezone();

    if (isEditing && reminder?.id) {
      // Update existing reminder
      const updatedReminder: UpdateReminder = {
        type: ReminderType.APPOINTMENT,
        subType: appointmentType,
        startDate,
        endDate,
        reminderFor: eventFor,
        eventDate: eventDateTime,
        eventTime,
        frequency,
        selectedDays: selectedDaysValue,
        frequencyValue,
        notes,
        timezone,
        tier: isCritical ? "CRITICAL" : "ROUTINE",
      };

      await updateReminder(reminder.id, updatedReminder);

      // Track reminder completion for edit case
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
        screenName: getReminderAppointmentScreenName(
          appointmentType,
          "complete"
        ),
      });
    } else {
      // Check if this is first reminder BEFORE adding
      const isFirstReminder = totalCount === 0;

      // Create new reminder
      const newReminder: CreateReminder = {
        type: ReminderType.APPOINTMENT,
        subType: appointmentType,
        startDate,
        endDate,
        reminderFor: eventFor,
        eventDate: eventDateTime,
        eventTime,
        frequency,
        selectedDays: selectedDaysValue,
        frequencyValue,
        notes,
        timezone,
        tier: isCritical ? "CRITICAL" : "ROUTINE",
      };

      try {
        await addReminder(newReminder);

        // Track reminder completion with first reminder flag
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
          screenName: getReminderAppointmentScreenName(
            appointmentType,
            "complete"
          ),
          ...(isFirstReminder && { action: "first_reminder_added" }),
        });
      } catch (err) {
        const errorMessage = getErrorMessage(err, "Failed to create reminder");
        showErrorToast(errorMessage);
        return;
      }
    }

    navigate("/reminders");
  };

  const isFinalStage =
    (appointmentType &&
      CategoryFlow[appointmentType][flowIndex] === ReminderInputStage.ADD) ||
    isEditing;
  return (
    <div
      className={`flex flex-col min-h-screen relative pb-[24px] ${
        isFinalStage ? styles.addStageBackgroundFinal : ""
      }`}
    >
      {/* Sticky wrapper so the topbar scrolls together with the sticky
          MHeader below it instead of scrolling away on its own — matches
          vitals' already-correct pattern (vitals/index.tsx). Without this,
          the topbar (relative, normal flow) scrolls out of view while
          MHeader stays pinned, leaving the two visibly out of sync. */}
      <div className="sticky top-0 z-[31]">
        <MTopBar
          hideProfilePicker
          exitPath={appointmentType ? "/reminders" : undefined}
          exitKey="appointment"
          withBackground={!isFinalStage}
          showPurpleGradient={isFinalStage}
          onBack={handleBack}
        />
      </div>

      <div
        className="profile-page-root flex flex-col "
        style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
      >
        <div className={styles.content}>
          {!appointmentType && (
            <>
              <div className={styles.cardContainer}>
                {paintHeader(
                  `${getPossessiveName(memberInfo, t)} ${t(
                    "reminders.appointment.typeLabelSuffix"
                  )}`,
                  t("reminders.appointment.description")
                )}
                <div className={styles.optionsContainer}>
                  {Object.values(ReminderSubType).map((option) => (
                    <MSelectCard
                      key={option}
                      text={t(`reminders.${option}.title`)}
                      selected={appointmentType === option}
                      onClick={() => handleAppointmentSelect(option)}
                      leftIcon={paintTypeIcon(option)}
                      variant="description"
                      description={t(`reminders.${option}.description`)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
          {appointmentType &&
            CategoryFlow[appointmentType][flowIndex] ===
              ReminderInputStage.START && (
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(
                  getScreenTitle(),
                  t("reminders.appointment.detailsScreenDescription")
                )}
                <div className="profile-input-container flex-1">
                  {paintStartInput()}
                </div>
                {/* Next button at the bottom right - following Figma design */}
                <div className="confirm-footer pt-0">
                  {paintNextButton(isStartInvalid)}
                </div>
              </div>
            )}
          {appointmentType &&
            CategoryFlow[appointmentType][flowIndex] ===
              ReminderInputStage.ADD && (
              <ReminderSummaryScreen
                memberInfo={memberInfo}
                title={getScreenTitle()}
                description={t(
                  "reminders.appointment.summaryScreenDescription"
                )}
                labelKey={appointmentType}
                recapIcon={getIcon(
                  appointmentType,
                  "var(--brand-colours-indigo-indigo-500, #8764BB)"
                )}
                recapIconClass={getTypeIconGradientClass(appointmentType)}
                recapLabel={getRecapLabel()}
                recapSecondaryText={getRecapSecondaryText()}
                recapSecondaryIcon={
                  appointmentType === ReminderSubType.THERAPY ? undefined : null
                }
                recapSecondaryTextClassName={
                  appointmentType === ReminderSubType.THERAPY
                    ? undefined
                    : styles.appointmentRecapCaption
                }
                reminderDate={reminderDate}
                setReminderDate={setReminderDate}
                showEndDate={
                  appointmentType === ReminderSubType.THERAPY &&
                  frequency !== ReminderFrequency.ONLY_ONCE
                }
                reminderEndDate={reminderEndDate}
                setReminderEndDate={setReminderEndDate}
                timeSlots={[
                  {
                    label: "",
                    value: reminderTime,
                    onChange: setReminderTime,
                    showBadge: false,
                  },
                ]}
                notes={notes}
                setNotes={setNotes}
                isInvalid={isAddInvalid()}
                onSubmit={handleCreateUpdateReminder}
              />
            )}
        </div>
      </div>
      {!appointmentType && <MFooterNavigation />}
    </div>
  );
};

export default AppointmentReminder;
